import React, { useState, useEffect, useMemo, useRef } from "react";

// ============================================================
// Plan + Price Tables (mirror UHRX agent form)
// ============================================================
const PLAN_TO_AMOUNT = {
  "Complete Solution":  39.95,
  "Sensible Solution":  29.95,
  "Essential Solution": 24.95,
  "Basic Solution":     19.95,
};
const SEMI_ANNUAL_PRICE = {
  "Complete Solution":  199.0,
  "Sensible Solution":  169.0,
  "Essential Solution": 129.0,
  "Basic Solution":     109.0,
};
const ANNUAL_PRICE = {
  "Complete Solution":  299.0,
  "Sensible Solution":  249.0,
  "Essential Solution": 199.0,
  "Basic Solution":     149.0,
};
const PLAN_NAMES = Object.keys(PLAN_TO_AMOUNT);
const CYCLE_OPTIONS = ["Monthly", "Semi-Annual", "Annual"];

// Plan benefits — mirror urgenthealthrx.com/plans/
const PLAN_BENEFITS = {
  "Basic Solution": {
    tagline: "Foundational benefits to get you started.",
    included: ["Hearing Aids", "Retail & Mail Pharmacy", "Vision"],
    inheritsFrom: null,
  },
  "Essential Solution": {
    tagline: "All Basic benefits plus expanded health coverage.",
    included: ["Alternative Medicine", "Durable Medical Equipment", "Lab Testing", "Mental Health", "MRI & CT Scans", "Vitamins"],
    inheritsFrom: "Basic Solution",
  },
  "Sensible Solution": {
    tagline: "All Essential benefits plus support and specialty care.",
    included: ["Caregiver Support", "Chiropractic", "Diabetic Supplies", "Physician & Hospital"],
    inheritsFrom: "Essential Solution",
  },
  "Complete Solution": {
    tagline: "Our most popular plan — everything you need.",
    included: ["Dental", "Doctors Online", "Health Advocates", "Pet Care"],
    inheritsFrom: "Sensible Solution",
    mostPopular: true,
  },
};

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

const STEPS = [
  { id: "plan",   label: "Plan",   tagline: "Pick the plan that fits" },
  { id: "member", label: "Member", tagline: "Tell us about you" },
  { id: "enroll", label: "Enroll", tagline: "Secure your savings today" },
];

// ============================================================
// Helpers
// ============================================================
const todayISO = () => new Date().toISOString().slice(0, 10);

const computeAmount = (plan, cycle) => {
  if (!plan) return 0;
  if (cycle === "Annual")      return ANNUAL_PRICE[plan]      || 0;
  if (cycle === "Semi-Annual") return SEMI_ANNUAL_PRICE[plan] || 0;
  return PLAN_TO_AMOUNT[plan] || 0;
};

const formatPhone = (raw) => {
  const d = (raw || "").replace(/\D/g, "").slice(0, 10);
  if (d.length < 4)  return d;
  if (d.length < 7)  return `(${d.slice(0,3)}) ${d.slice(3)}`;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
};

const formatCard = (raw) => (raw || "").replace(/\D/g, "").slice(0, 19).replace(/(.{4})/g, "$1 ").trim();

// ============================================================
// Styling tokens
// ============================================================
const colors = {
  primary:  "#0c4a6e",
  accent:   "#0ea5e9",
  text:     "#111827",
  muted:    "#6b7280",
  border:   "#e5e7eb",
  bgCard:   "#ffffff",
  bgPage:   "#f3f4f6",
  bgPanel:  "#f9fafb",
  error:    "#b91c1c",
  success:  "#15803d",
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  fontSize: 16,
  background: "#ffffff",
  color: colors.text,
  outline: "none",
};

const labelStyle = { display: "block", fontSize: 13, fontWeight: 600, color: colors.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 };

// ============================================================
// Main App
// ============================================================
export default function App() {
  const [openStep, setOpenStep] = useState("plan");
  const [completed, setCompleted] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverMsg, setServerMsg] = useState(null);
  const [geoCoords, setGeoCoords] = useState({ latitude: "", longitude: "" });

  const [form, setForm] = useState({
    Plan_Name: "",
    billing_cycle: "",  // empty default — user must explicitly select
    First_Name: "", Last_Name: "", Date_of_Birth: "", Email: "", Phone: "",
    Address_Line_1: "", Address_Line_2: "", City: "", State: "", Zip_Code: "", Country: "United States",
    Payment_Method: "",
    card_number: "", exp_month: "", exp_year: "", cvv: "",
    routing_number: "", account_number: "", confirm_account_number: "", account_type: "checking",
    auth_accepted: false,
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const amount = useMemo(() => computeAmount(form.Plan_Name, form.billing_cycle), [form.Plan_Name, form.billing_cycle]);

  // Browser geolocation on mount (failsafe)
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setGeoCoords({ latitude: String(pos.coords.latitude), longitude: String(pos.coords.longitude) }),
      () => {},
      { timeout: 30000, enableHighAccuracy: false, maximumAge: 60000 }
    );
  }, []);

  // Iframe auto-resize — post content height to parent on every layout change.
  // Parent listens for {type:"enroll-uhrx-height", height} and adjusts iframe.
  // Measure the React root div directly (not documentElement) — inside a fixed-height
  // iframe, documentElement.scrollHeight reports the iframe viewport, not the content.
  useEffect(() => {
    if (typeof window === "undefined" || window.parent === window) return;
    if (typeof ResizeObserver === "undefined") return;
    const root = document.getElementById("root");
    if (!root) return;
    const post = () => {
      try {
        const h = root.getBoundingClientRect().height;
        window.parent.postMessage(
          { type: "enroll-uhrx-height", height: Math.ceil(h) },
          "*"
        );
      } catch {}
    };
    const ro = new ResizeObserver(post);
    ro.observe(root);
    post();
    return () => ro.disconnect();
  }, []);

  // ZIP autofill (city/state) — backend expects ?z= param, returns full state name
  const lookupZip = async (zip) => {
    if (!zip || zip.length !== 5) return;
    try {
      const r = await fetch(`/api/zip_lookup?z=${zip}`);
      if (!r.ok) return;
      const j = await r.json();
      if (j && j.ok) {
        if (j.city)  update("City", j.city);
        if (j.state) update("State", j.state);
        update("Country", "United States");
      }
    } catch {}
  };

  // Routing-number → bank-name lookup
  const [bankName, setBankName] = useState("");
  const [bankLookupErr, setBankLookupErr] = useState("");
  const lookupRouting = async (rn) => {
    setBankName("");
    setBankLookupErr("");
    if (!rn || rn.length !== 9) return;
    try {
      const r = await fetch(`/api/routing_lookup?r=${rn}`);
      const j = await r.json();
      if (j && j.ok && j.bank_name) {
        setBankName(j.bank_name);
        update("bank_name", j.bank_name);  // also stash on form for backend
      } else {
        setBankLookupErr("Routing number not recognized. Please double-check.");
      }
    } catch {
      setBankLookupErr("Could not verify routing number. Please try again.");
    }
  };

  // Section navigation lock — can only open a section if all prior sections are complete
  const canOpenStep = (stepId) => {
    const idx = STEPS.findIndex((s) => s.id === stepId);
    for (let i = 0; i < idx; i++) {
      if (!completed[STEPS[i].id]) return false;
    }
    return true;
  };

  // Step validation — plan requires both plan AND billing_cycle to be explicitly chosen.
  // Enroll step combines payment-method validity + auth checkbox + geo capture.
  const paymentValid = form.Payment_Method === "Card"
              ? (form.card_number.replace(/\s/g, "").length >= 13 && !!form.exp_month && !!form.exp_year && form.cvv.length >= 3)
              : form.Payment_Method === "ACH"
                ? (form.routing_number.length === 9 && !!form.account_number && form.account_number === form.confirm_account_number)
                : false;
  const stepValid = {
    plan:    !!form.Plan_Name && !!form.billing_cycle && amount > 0,
    member:  !!form.First_Name && !!form.Last_Name && !!form.Date_of_Birth && !!form.Email
              && form.Phone.replace(/\D/g, "").length === 10
              && !!form.Address_Line_1 && !!form.City && !!form.State && form.Zip_Code.length === 5,
    enroll:  paymentValid && form.auth_accepted && !!geoCoords.latitude && !!geoCoords.longitude,
  };

  // Auto-advance: when current step becomes valid AND has not been advanced past, move to next
  // 500ms debounce so user gets a moment to see their selection register.
  const advancingRef = useRef(false);
  useEffect(() => {
    if (advancingRef.current) return;
    if (openStep === "enroll") return;          // enroll is the final step; stays manual
    if (!stepValid[openStep])  return;
    if (completed[openStep])   return;          // already advanced past this once
    advancingRef.current = true;
    const t = setTimeout(() => {
      setCompleted((c) => ({ ...c, [openStep]: true }));
      const idx = STEPS.findIndex((s) => s.id === openStep);
      if (idx < STEPS.length - 1) setOpenStep(STEPS[idx + 1].id);
      advancingRef.current = false;
    }, 500);
    return () => { clearTimeout(t); advancingRef.current = false; };
  }, [openStep, stepValid.plan, stepValid.member, completed]);

  const handleSubmit = async () => {
    if (!stepValid.enroll || submitting) return;
    setSubmitting(true);
    setServerMsg(null);

    const payload = {
      ...form,
      amount,
      payment_type: form.Payment_Method,
      Phone: form.Phone.replace(/\D/g, ""),
      Plan_Identification: form.Plan_Name,
      Enrollment_Date: todayISO(),
      Latitude:  geoCoords.latitude,
      Longitude: geoCoords.longitude,
      origin: "online",
      auth_timestamp: new Date().toISOString(),
      auth_user_agent: navigator.userAgent,
    };

    try {
      const r = await fetch("/api/enroll/uhrx/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const j = await r.json();
      if (r.ok && j.status === "success") {
        setServerMsg({ type: "ok", text: "Thank you! Your enrollment is complete. A confirmation email is on its way." });
        setCompleted((c) => ({ ...c, enroll: true }));
        setOpenStep("");
      } else {
        setServerMsg({ type: "err", text: j.message || "Submission failed. Please try again." });
      }
    } catch (e) {
      setServerMsg({ type: "err", text: "Network error. Please try again." });
    }
    setSubmitting(false);
  };

  const activeIdx = STEPS.findIndex((s) => s.id === openStep);

  return (
    <div style={{ background: "transparent", padding: "8px 0" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        {/* Horizontal stepper — progress at a glance, no expand/contract */}
        <div style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "8px 4px 24px",
          position: "relative",
        }}>
          {/* Connecting progress line behind the circles */}
          <div style={{
            position: "absolute",
            top: 22, left: 36, right: 36, height: 2,
            background: colors.border, zIndex: 0,
          }} />
          <div style={{
            position: "absolute",
            top: 22, left: 36,
            width: `calc((100% - 72px) * ${Math.max(0, activeIdx) / (STEPS.length - 1)})`,
            height: 2, background: colors.success, zIndex: 0,
            transition: "width 250ms ease",
          }} />

          {STEPS.map((step, i) => {
            const isActive = openStep === step.id;
            const isComplete = !!completed[step.id];
            const canOpen = canOpenStep(step.id);
            const circleBg = isComplete ? colors.success : (isActive ? colors.accent : "#ffffff");
            const circleColor = isComplete || isActive ? "#ffffff" : colors.muted;
            const circleBorder = isComplete ? colors.success : (isActive ? colors.accent : colors.border);
            const labelColor = isActive ? colors.primary : (isComplete ? colors.success : colors.muted);

            return (
              <button
                key={step.id}
                type="button"
                onClick={() => { if (canOpen) setOpenStep(step.id); }}
                disabled={!canOpen}
                style={{
                  flex: "0 0 auto",
                  background: "none", border: "none", padding: 0,
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
                  cursor: canOpen ? "pointer" : "default",
                  position: "relative", zIndex: 1,
                  width: 72,
                }}
              >
                <span style={{
                  width: 44, height: 44, borderRadius: "50%",
                  background: circleBg,
                  color: circleColor,
                  border: `2px solid ${circleBorder}`,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontSize: 16, fontWeight: 700,
                  boxShadow: isActive ? "0 0 0 4px rgba(14,165,233,0.18)" : "none",
                  transition: "all 150ms ease",
                }}>
                  {isComplete ? "✓" : i + 1}
                </span>
                <span style={{
                  fontSize: 12, fontWeight: 700, color: labelColor,
                  textTransform: "uppercase", letterSpacing: 0.4,
                }}>
                  {step.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Active step content */}
        <section style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: 12,
          padding: 28,
          boxShadow: "0 4px 16px rgba(12, 74, 110, 0.05)",
        }}>
          {openStep === "plan"   && <PlanSection   form={form} update={update} amount={amount} tagline={STEPS[0].tagline} />}
          {openStep === "member" && <MemberSection form={form} update={update} lookupZip={lookupZip} tagline={STEPS[1].tagline} />}
          {openStep === "enroll" && <EnrollSection form={form} update={update} amount={amount} bankName={bankName} bankLookupErr={bankLookupErr} lookupRouting={lookupRouting} geoCoords={geoCoords} stepValid={stepValid} submitting={submitting} onSubmit={handleSubmit} tagline={STEPS[2].tagline} />}
        </section>

        {serverMsg && (
          <div style={{
            marginTop: 16, padding: "16px 20px", borderRadius: 12,
            background: serverMsg.type === "ok" ? "#dcfce7" : "#fee2e2",
            color: serverMsg.type === "ok" ? colors.success : colors.error,
            fontWeight: 600, fontSize: 16, textAlign: "center",
          }}>{serverMsg.text}</div>
        )}

      </div>
    </div>
  );
}

// ============================================================
// Section Components — no Continue buttons (auto-advance)
// ============================================================
function StepHeader({ tagline }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 22, paddingBottom: 18, borderBottom: `1px solid ${colors.border}` }}>
      <img src="/uhrx-logo.jpg" alt="" width={48} height={48} style={{ flexShrink: 0, borderRadius: 6, display: "block" }} />
      <h2 style={{ fontSize: 22, fontWeight: 600, color: colors.primary, margin: 0, lineHeight: 1.2 }}>{tagline}</h2>
    </div>
  );
}

function PlanSection({ form, update, amount, tagline }) {
  return (
    <div>
      <StepHeader tagline={tagline} />
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Plan</label>
        <select value={form.Plan_Name} onChange={(e) => update("Plan_Name", e.target.value)} style={inputStyle}>
          <option value="">— Select a plan —</option>
          {PLAN_NAMES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Billing Cycle</label>
        <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(3, 1fr)" }}>
          {CYCLE_OPTIONS.map((c) => {
            const selected = form.billing_cycle === c;
            return (
              <button key={c} type="button" onClick={() => update("billing_cycle", c)} style={{
                padding: "10px 8px", borderRadius: 8,
                border: `2px solid ${selected ? colors.accent : colors.border}`,
                background: selected ? "#e0f2fe" : "#ffffff",
                color: selected ? colors.primary : colors.text,
                fontWeight: 600,
              }}>{c}</button>
            );
          })}
        </div>
      </div>
      {form.Plan_Name && (() => {
        const benefits = PLAN_BENEFITS[form.Plan_Name];
        // Flatten the full benefit list for the selected plan (inheritance is hidden from the user)
        const fullBenefits = [];
        let cur = form.Plan_Name;
        while (cur) {
          const t = PLAN_BENEFITS[cur];
          if (t) fullBenefits.unshift(...t.included);  // Basic first, then accumulating
          cur = t?.inheritsFrom || null;
        }
        return (
          <div style={{ background: colors.bgPanel, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
              <div>
                <div style={{ color: colors.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600 }}>Your selection</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: colors.primary, marginTop: 4 }}>
                  {form.Plan_Name} {benefits?.mostPopular && <span style={{ marginLeft: 6, fontSize: 11, padding: "2px 8px", background: colors.accent, color: "#ffffff", borderRadius: 999, fontWeight: 700, letterSpacing: 0.4 }}>MOST POPULAR</span>}
                </div>
                {form.billing_cycle && (
                  <div style={{ color: colors.muted, fontSize: 13, marginTop: 2 }}>{form.billing_cycle}</div>
                )}
              </div>
              {form.billing_cycle && (
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 26, fontWeight: 800, color: colors.text, lineHeight: 1 }}>${amount.toFixed(2)}</div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: colors.muted, marginTop: 4 }}>
                    {form.billing_cycle === "Monthly" ? "per month" : form.billing_cycle === "Semi-Annual" ? "every 6 months" : "per year"}
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${colors.border}` }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: colors.muted, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 10 }}>Benefits Included</div>
              <ul style={{
                margin: 0, padding: 0, listStyle: "none",
                columnCount: 2, columnGap: 24,
                fontSize: 14, color: colors.text, lineHeight: 1.7,
              }}>
                {fullBenefits.map((b) => (
                  <li key={b} style={{ breakInside: "avoid", marginBottom: 4, display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span style={{ color: colors.success, fontWeight: 700, flexShrink: 0 }}>✓</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function MemberSection({ form, update, lookupZip, tagline }) {
  return (
    <div>
      <StepHeader tagline={tagline} />
      {/* Personal */}
      <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${colors.border}` }}>
        <div style={{ ...labelStyle, color: colors.primary, fontSize: 12, marginBottom: 12 }}>Personal Details</div>
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr", marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>First Name</label>
            <input value={form.First_Name} onChange={(e) => update("First_Name", e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Last Name</label>
            <input value={form.Last_Name} onChange={(e) => update("Last_Name", e.target.value)} style={inputStyle} />
          </div>
        </div>
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr", marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Date of Birth</label>
            <input type="date" value={form.Date_of_Birth} onChange={(e) => update("Date_of_Birth", e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Phone</label>
            <input value={formatPhone(form.Phone)} onChange={(e) => update("Phone", e.target.value)} placeholder="(555) 123-4567" style={inputStyle} />
          </div>
        </div>
        <div>
          <label style={labelStyle}>Email</label>
          <input type="email" value={form.Email} onChange={(e) => update("Email", e.target.value)} style={inputStyle} />
        </div>
      </div>

      {/* Mailing Address */}
      <div>
        <div style={{ ...labelStyle, color: colors.primary, fontSize: 12, marginBottom: 12 }}>Mailing Address</div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Address Line 1</label>
          <input value={form.Address_Line_1} onChange={(e) => update("Address_Line_1", e.target.value)} style={inputStyle} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Address Line 2 <span style={{ color: colors.muted, textTransform: "none" }}>(optional)</span></label>
          <input value={form.Address_Line_2} onChange={(e) => update("Address_Line_2", e.target.value)} style={inputStyle} />
        </div>
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 2fr", marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>ZIP</label>
            <input
              value={form.Zip_Code}
              maxLength={5}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 5);
                update("Zip_Code", v);
                if (v.length === 5) lookupZip(v);
              }}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>City</label>
            <input value={form.City} onChange={(e) => update("City", e.target.value)} style={inputStyle} />
          </div>
        </div>
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr" }}>
          <div>
            <label style={labelStyle}>State</label>
            <input value={form.State} onChange={(e) => update("State", e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Country</label>
            <input value={form.Country} onChange={(e) => update("Country", e.target.value)} style={inputStyle} />
          </div>
        </div>
      </div>
    </div>
  );
}

function EnrollSection({ form, update, amount, bankName, bankLookupErr, lookupRouting, geoCoords, stepValid, submitting, onSubmit, tagline }) {
  const months = ["01","02","03","04","05","06","07","08","09","10","11","12"];
  const yr = new Date().getFullYear();
  const years = Array.from({ length: 15 }, (_, i) => String(yr + i));
  const locOK = !!(geoCoords.latitude && geoCoords.longitude);
  const [termsOpen, setTermsOpen] = React.useState(false);

  return (
    <div>
      <StepHeader tagline={tagline} />

      {/* Order Summary */}
      <div style={{ background: colors.bgPanel, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 16, marginBottom: 18, fontSize: 14 }}>
        <div style={{ fontWeight: 700, color: colors.primary, marginBottom: 8 }}>Order Summary</div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span>Plan</span><span>{form.Plan_Name} ({form.billing_cycle})</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span>Customer</span><span>{form.First_Name} {form.Last_Name}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, paddingTop: 8, borderTop: `1px solid ${colors.border}`, fontSize: 16, fontWeight: 700 }}>
          <span>Amount Today</span><span>${amount.toFixed(2)}</span>
        </div>
      </div>

      {/* Payment Method */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Payment Method</label>
        <div style={{ display: "grid", gap: 8, gridTemplateColumns: "1fr 1fr" }}>
          {["Card", "ACH"].map((m) => {
            const selected = form.Payment_Method === m;
            return (
              <button key={m} type="button" onClick={() => update("Payment_Method", m)} style={{
                padding: "12px 8px", borderRadius: 8,
                border: `2px solid ${selected ? colors.accent : colors.border}`,
                background: selected ? "#e0f2fe" : "#ffffff",
                color: selected ? colors.primary : colors.text,
                fontWeight: 600,
              }}>{m === "Card" ? "Credit / Debit Card" : "Bank Account (ACH)"}</button>
            );
          })}
        </div>
      </div>

      {form.Payment_Method === "Card" && (
        <div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Card Number</label>
            <input value={formatCard(form.card_number)} onChange={(e) => update("card_number", e.target.value.replace(/\s/g, ""))} placeholder="1234 5678 9012 3456" style={inputStyle} />
          </div>
          <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr 1fr" }}>
            <div>
              <label style={labelStyle}>Exp Month</label>
              <select value={form.exp_month} onChange={(e) => update("exp_month", e.target.value)} style={inputStyle}>
                <option value="">MM</option>
                {months.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Exp Year</label>
              <select value={form.exp_year} onChange={(e) => update("exp_year", e.target.value)} style={inputStyle}>
                <option value="">YYYY</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>CVV</label>
              <input value={form.cvv} maxLength={4} onChange={(e) => update("cvv", e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="123" style={inputStyle} />
            </div>
          </div>
        </div>
      )}

      {form.Payment_Method === "ACH" && (
        <div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Routing Number (9 digits)</label>
            <input
              value={form.routing_number}
              maxLength={9}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 9);
                update("routing_number", v);
                if (v.length === 9) lookupRouting(v);
              }}
              style={inputStyle}
            />
            {bankName && (
              <div style={{ marginTop: 6, padding: "8px 10px", background: "#dcfce7", color: colors.success, fontSize: 13, borderRadius: 6 }}>
                ✓ Bank: <strong>{bankName}</strong>
              </div>
            )}
            {bankLookupErr && !bankName && (
              <div style={{ marginTop: 6, padding: "8px 10px", background: "#fee2e2", color: colors.error, fontSize: 13, borderRadius: 6 }}>
                {bankLookupErr}
              </div>
            )}
          </div>
          <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr", marginBottom: 12 }}>
            <div>
              <label style={labelStyle}>Account Number</label>
              <input value={form.account_number} onChange={(e) => update("account_number", e.target.value.replace(/\D/g, ""))} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Confirm Account Number</label>
              <input value={form.confirm_account_number} onChange={(e) => update("confirm_account_number", e.target.value.replace(/\D/g, ""))} style={inputStyle} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Account Type</label>
            <div style={{ display: "grid", gap: 8, gridTemplateColumns: "1fr 1fr" }}>
              {["checking", "savings"].map((t) => {
                const selected = form.account_type === t;
                return (
                  <button key={t} type="button" onClick={() => update("account_type", t)} style={{
                    padding: "10px 8px", borderRadius: 8,
                    border: `2px solid ${selected ? colors.accent : colors.border}`,
                    background: selected ? "#e0f2fe" : "#ffffff",
                    color: selected ? colors.primary : colors.text,
                    fontWeight: 600, textTransform: "capitalize",
                  }}>{t}</button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 22, paddingTop: 22, borderTop: `1px solid ${colors.border}` }} />

      <label style={{ display: "flex", alignItems: "center", gap: 12, padding: 14, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 8, cursor: "pointer", background: form.auth_accepted ? "#f0fdf4" : "#ffffff" }}>
        <input type="checkbox" checked={form.auth_accepted} onChange={(e) => update("auth_accepted", e.target.checked)} style={{ width: 18, height: 18, flexShrink: 0 }} />
        <span style={{ fontSize: 15, color: colors.text, fontWeight: 600 }}>
          I Agree to the{" "}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setTermsOpen((o) => !o); }}
            style={{ background: "none", border: "none", padding: 0, color: colors.accent, fontWeight: 600, fontSize: 15, textDecoration: "underline", cursor: "pointer" }}
          >
            Terms &amp; Authorization
          </button>
        </span>
      </label>

      {termsOpen && (
        <div style={{ background: "#f9fafb", border: `1px solid ${colors.border}`, borderRadius: 8, padding: 16, marginBottom: 16, fontSize: 13, lineHeight: 1.6, color: colors.text }}>
          <div style={{ fontWeight: 700, color: colors.primary, marginBottom: 6 }}>Recurring Billing Authorization</div>
          <p style={{ margin: "0 0 12px" }}>
            I authorize Urgent Health Rx to charge the payment method above for the amount shown today and on a recurring basis according to the billing cycle I selected. Charges will appear on my statement as <strong>Urgent Health Rx</strong>. I understand I can cancel anytime by calling <strong>1-833-932-2479</strong> or emailing <strong>support@urgenthealthrx.com</strong>.
          </p>

          <div style={{ fontWeight: 700, color: colors.primary, marginBottom: 6 }}>Electronic Communication Consent</div>
          <p style={{ margin: "0 0 12px" }}>
            By submitting this enrollment, I AGREE TO BE CONTACTED at the telephone number and/or email address I have provided, regardless if this number is a cell phone, residential line, and/or is listed on the National or State Do Not Call Registry. Urgent Health Rx and/or its affiliates may contact me by prerecorded calls, automated text messages (SMS/MMS), Avatar/Ringless Voicemail, or other automatic dialer technology regarding my account, billing, and service updates. I confirm the information I've provided is accurate and consent to receive electronic communications pursuant to this agreement.
          </p>

          <div style={{ fontWeight: 700, color: colors.primary, marginBottom: 6 }}>Customer Support</div>
          <p style={{ margin: 0 }}>
            Questions about your subscription? Call <strong>1-833-932-2479</strong> or email <strong>support@urgenthealthrx.com</strong>.
          </p>
        </div>
      )}

      {!locOK && (
        <div style={{ background: "#fef3c7", color: "#92400e", padding: "12px 14px", borderRadius: 8, marginBottom: 12, fontSize: 14 }}>
          Location must be captured to submit. Please allow location access when your browser prompts.
        </div>
      )}

      <button type="button" onClick={onSubmit} disabled={!stepValid.enroll || submitting} style={{
        width: "100%",
        padding: "14px 20px",
        borderRadius: 10,
        border: "none",
        background: !stepValid.enroll || submitting ? "#94a3b8" : colors.primary,
        color: "#ffffff",
        fontSize: 16,
        fontWeight: 700,
      }}>
        {submitting ? "Submitting..." : `Authorize & Enroll — $${amount.toFixed(2)}`}
      </button>
    </div>
  );
}
